import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { listSummaries, listPolicyClauses, suggestLadder, issueWarning, issueLearningPoint } from '../lib/db.js';
import { useAuth } from '../lib/auth.jsx';
import { StatusBar } from '../components/StatusBar.jsx';
import { BottomNav } from '../components/BottomNav.jsx';

const LEVELS = [
  { n: 1, label: 'วาจา', name: 'verbal' },
  { n: 2, label: 'ลายลักษณ์', name: 'written' },
  { n: 3, label: 'ครั้งสุดท้าย', name: 'final' },
  { n: 4, label: 'เลิกจ้าง', name: 'terminate' },
];
const today = () => new Date().toISOString().slice(0, 10);

export function IssueWarning() {
  const { appUser } = useAuth();
  const [params] = useSearchParams();
  const [docType, setDocType] = useState('warning'); // 'warning' | 'learning'
  const [emps, setEmps] = useState([]);
  const [empId, setEmpId] = useState('');
  const [clauses, setClauses] = useState([]);
  const [clauseId, setClauseId] = useState('');
  const [suggest, setSuggest] = useState(null);   // { level, prior_count }
  const [level, setLevel] = useState(1);
  const [date, setDate] = useState(today());
  const [detail, setDetail] = useState('');
  const [topic, setTopic] = useState('');
  const [followUp, setFollowUp] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState('');

  // header + toggle stay put; the reserved-height area below swaps content instantly
  function switchType(t) { setDocType(t); setResult(null); }

  useEffect(() => {
    Promise.all([listSummaries(), listPolicyClauses()])
      .then(([e, c]) => {
        setEmps(e);
        setClauses(c);
        setEmpId(params.get('emp') && e.some((x) => x.id === params.get('emp')) ? params.get('emp') : e[0]?.id || '');
        setClauseId(c[0]?.id || '');
      })
      .catch((ex) => setErr(ex.message));
  }, []);

  const clause = clauses.find((c) => c.id === clauseId);
  const emp = emps.find((e) => e.id === empId);
  const empName = emp ? (emp.nickname || `${emp.first_name} ${emp.last_name}`) : '';

  // recompute the suggested ladder level whenever employee or clause changes
  useEffect(() => {
    if (!empId || !clause) return;
    let active = true;
    suggestLadder(empId, clause.category)
      .then((s) => { if (active) { setSuggest(s); setLevel(s.level); } })
      .catch(() => {});
    return () => { active = false; };
  }, [empId, clauseId]);

  async function onIssue() {
    if (!empId) { setErr('เลือกพนักงานก่อน'); return; }
    setErr('');
    setBusy(true);
    try {
      if (docType === 'learning') {
        if (!topic.trim()) { setErr('กรอกหัวข้อ Learning point ก่อน'); setBusy(false); return; }
        const rec = await issueLearningPoint({
          employee_id: empId, topic: topic.trim(), detail: detail.trim(),
          follow_up_date: followUp || null, coached_by: appUser?.id,
        });
        setResult({ type: 'learning', empName, id: rec.id });
        setTopic(''); setDetail(''); setFollowUp('');
      } else {
        if (!clause) { setErr('เลือกข้อบังคับก่อน'); setBusy(false); return; }
        if (!detail.trim()) { setErr('กรอกรายละเอียดเหตุการณ์ก่อน'); setBusy(false); return; }
        const lv = LEVELS.find((l) => l.n === level);
        const rec = await issueWarning({
          employee_id: empId, category: clause.category, ladder_level: level, level_name: lv?.name,
          policy_clause: `${clause.clause_no} — ${clause.title}`, policy_clause_id: clause.id,
          incident_date: date, incident_detail: detail.trim(),
          issued_by: appUser?.id, issued_by_name: 'หัวหน้า',
        });
        setResult({ type: 'warning', empName, id: rec.id, level });
        setDetail('');
      }
    } catch (e) { setErr(e.message); }
    setBusy(false);
  }

  return (
    <div className="phone">
      <StatusBar />
      <section className="screen">
        <div className="title-row">
          <div>
            <div className="eyebrow">ออกเอกสารวินัย</div>
            <h1 className="page">ออกเอกสาร</h1>
          </div>
          <span className="glyph">✎</span>
        </div>

        <div className="seg" style={{ marginTop: 4 }}>
          <button aria-pressed={docType === 'warning'} onClick={() => switchType('warning')}>ออกใบเตือน</button>
          <button aria-pressed={docType === 'learning'} onClick={() => switchType('learning')}>Learning point</button>
        </div>
        <p className="note" style={{ marginTop: 8, minHeight: 38 }}>
          {docType === 'learning'
            ? 'บันทึกเชิงพัฒนา/สอนงาน — ไม่ใช่โทษทางวินัย ไม่มีขั้น ladder'
            : 'มีผลทางวินัย · มี ladder + อายุ 1 ปี · พนักงานต้องรับทราบ'}
        </p>

        {result && result.type === 'warning' && (
          <div className="ok-banner" style={{ background: 'color-mix(in srgb,var(--pend) 12%,transparent)', borderColor: 'color-mix(in srgb,var(--pend) 35%,transparent)' }}>
            <div className="l1">ออกใบเตือนขั้นที่ {result.level} ให้ {result.empName} แล้ว</div>
            <div className="note">
              รอพนักงานรับทราบ · เขียน audit แล้ว ·{' '}
              <Link to={`/warnings/${result.id}`}>เปิดใบเตือน</Link> ·{' '}
              <Link to={`/warnings/${result.id}/print`}>พิมพ์</Link>
            </div>
          </div>
        )}
        {result && result.type === 'learning' && (
          <div className="ok-banner">
            <div className="l1">✓ บันทึก Learning point ให้ {result.empName} แล้ว</div>
            <div className="note">เก็บไว้ในไทม์ไลน์เชิงพัฒนา (ไม่ใช่โทษทางวินัย) · เขียน audit แล้ว</div>
          </div>
        )}

        <label className="f">พนักงาน</label>
        <select value={empId} onChange={(e) => setEmpId(e.target.value)}>
          {emps.map((e) => (
            <option key={e.id} value={e.id}>{(e.nickname || e.first_name)} · {e.position} · {e.branch}</option>
          ))}
        </select>

        {/* mode-specific fields — reserved height so the header/toggle/submit stay put */}
        <div className="mode-fields">
          {docType === 'warning' ? (
            <div className="mode-pane">
              <label className="f">อ้างอิงข้อบังคับ (ที่ยื่นแล้ว)</label>
              <select value={clauseId} onChange={(e) => setClauseId(e.target.value)}>
                {clauses.map((c) => <option key={c.id} value={c.id}>{c.clause_no} — {c.title}</option>)}
              </select>

              <label className="f">ขั้นการตักเตือน (ระบบเสนอ — หัวหน้ายืนยัน)</label>
              <div className="ladder">
                {LEVELS.map((l) => (
                  <button key={l.n} type="button"
                    className={`step${l.n <= level ? ' on' : ''}${l.n === level ? ' cur' : ''}`}
                    style={{ border: 0, cursor: 'pointer' }}
                    onClick={() => setLevel(l.n)}>
                    <span className="n">{l.n}</span>{l.label}
                  </button>
                ))}
              </div>
              {suggest && (
                <p className="note" style={{ marginTop: 10 }}>
                  ระบบเสนอ <b style={{ color: 'var(--t1)' }}>ขั้นที่ {suggest.level} ({LEVELS[suggest.level - 1].label})</b>
                  {suggest.prior_count > 0
                    ? ` — เพราะมีใบเตือนหมวดนี้ที่ยังมีผล ${suggest.prior_count} ฉบับ`
                    : ' — ยังไม่เคยมีใบเตือนหมวดนี้'} · แตะเพื่อปรับขั้นเองได้
                </p>
              )}
              {level === 4 && (
                <p className="note" style={{ marginTop: 8, color: 'var(--warn)' }}>
                  ⚠︎ ขั้นเลิกจ้าง — ควรปรึกษาทนายแรงงานและตรวจ ladder ให้ครบก่อนเสมอ
                </p>
              )}
              <label className="f">วันที่เกิดเหตุ</label>
              <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
              <label className="f">รายละเอียดเหตุการณ์</label>
              <textarea value={detail} onChange={(e) => setDetail(e.target.value)}
                placeholder="เช่น มาทำงานสาย 35 นาที โดยไม่แจ้งล่วงหน้า เป็นครั้งที่ 3 ในเดือนนี้…" />
              {detail.trim() && clause && (
                <div className="card">
                  <div className="eyebrow" style={{ marginBottom: 6 }}>ตัวอย่างใบเตือน</div>
                  <div className="l1" style={{ fontSize: 16, fontWeight: 700 }}>หนังสือตักเตือน — ขั้นที่ {level} ({LEVELS[level - 1].label})</div>
                  <div className="note" style={{ marginTop: 8 }}>
                    เรียน คุณ {empName} · ตามที่ท่านได้ {detail.trim()} อันเป็นการฝ่าฝืน{clause?.clause_no} — {clause?.title}
                    บริษัทจึงออกหนังสือตักเตือน (มีผล 1 ปีนับจากวันเกิดเหตุ)
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="mode-pane">
              <div className="leave-note" style={{ background: 'var(--surface-2)' }}>
                <b>Learning point</b> คือบันทึกเชิงพัฒนา/สอนงาน เก็บในไทม์ไลน์ของพนักงาน —
                ไม่ใช่โทษทางวินัย ไม่มีขั้น ladder และไม่ต้องให้พนักงานเซ็นรับทราบ
                เหมาะกับการโค้ชเรื่องเล็ก ๆ ก่อนถึงขั้นต้องออกใบเตือน
              </div>
              <label className="f">หัวข้อ / เรื่องที่สอน</label>
              <input type="text" value={topic} onChange={(e) => setTopic(e.target.value)}
                placeholder="เช่น การจัดเก็บสินค้าให้ปลอดภัย" />
              <label className="f">รายละเอียด / แนวทางพัฒนา</label>
              <textarea value={detail} onChange={(e) => setDetail(e.target.value)}
                placeholder="สิ่งที่แนะนำและจะติดตามผลร่วมกัน…" />
              <label className="f">วันติดตามผล (ไม่บังคับ)</label>
              <input type="date" value={followUp} onChange={(e) => setFollowUp(e.target.value)} />
              {topic.trim() && (
                <div className="card">
                  <div className="eyebrow" style={{ marginBottom: 6 }}>ตัวอย่าง Learning point</div>
                  <div className="l1" style={{ fontSize: 16, fontWeight: 700 }}>{topic.trim()}</div>
                  <div className="note" style={{ marginTop: 8 }}>
                    บันทึกเชิงพัฒนาให้ {empName}{detail.trim() ? ` — ${detail.trim()}` : ''}
                    {followUp ? ` · ติดตามผล ${followUp}` : ''}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {err && <p className="err" style={{ marginTop: 14 }}>{err}</p>}

        <button className="cta" onClick={onIssue} disabled={busy}>
          {busy ? 'กำลังบันทึก…' : docType === 'learning' ? 'บันทึก Learning point' : 'ออกใบเตือน · ให้พนักงานรับทราบ'}
        </button>
        <p className="note" style={{ marginTop: 14, textAlign: 'center' }}>
          {docType === 'learning'
            ? 'บันทึกด้านบวก/พัฒนา เก็บคู่กับด้านลบ เพื่อความเป็นธรรม'
            : 'ทุกใบเตือนต้องมีคนกดออก + เขียน audit · พนักงานรับทราบ/ปฏิเสธได้'}
        </p>
      </section>
      <BottomNav />
    </div>
  );
}
